import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef } from 'react'

import Chip from '@/components/ui/Chip'
import { useRovingTabs } from '@/components/ui/use-roving-tabs'
import { useSlidingIndicator } from '@/components/ui/use-sliding-indicator'
import { revealMotion } from '@/lib/motion/reveal'
import { cn } from '@/lib/utils'

export interface TabBarOption {
  value: string
  label: string
}

interface TabBarProps {
  options: readonly TabBarOption[]
  value: string
  onChange: (value: string) => void
  ariaLabel: string
  /**
   * Which of the two tab kinds this is — see the component note for the rule. `pills` is the
   * pre-refresh name for `boxed`, kept so existing callers compile; it renders exactly as
   * `boxed` does, and new code should say `boxed`.
   */
  variant?: 'underline' | 'boxed' | 'pills'
  /** `boxed`-only — `underline` never reads it. Forwarded to Chip's own `size`: `md` 36px,
   *  `sm` the compact 32px. */
  size?: 'md' | 'sm'
  /**
   * Opt-in tab ↔ panel wiring. With it, each tab gets an `id` and `aria-controls` pointing at the
   * one panel a caller renders as `<TabPanel idBase={…} value={…}>`, so a screen reader announces
   * the panel a tab switches. Without it the tabs render as before, with no ids.
   */
  idBase?: string
  /**
   * On the control's outer box: its margins and its place in a flex or grid row. Survives at
   * every width, since the strip's own vertical room lives on the strip inside it.
   */
  className?: string
  /**
   * On the strip itself, the scrolling `tablist`: the padding of a strip that bleeds through the
   * shell gutter (`px-shell-x`), or a boxed strip's `lg:flex-wrap`.
   */
  stripClassName?: string
}

/** The bar's border width, so it sits on the tab's bottom edge. */
const BAR_THICKNESS = 3
const tabId = (idBase: string, value: string) => `${idBase}-tab-${value}`
const panelId = (idBase: string) => `${idBase}-panel`

/**
 * The tabs inside a page, of two kinds, by one rule (the 2026-09-28 design refresh):
 *
 * - `underline` (the default) switches the VIEW — Floorsheet's By broker / By symbol / All trades,
 *   a course's sections. A 16px label that turns foreground 600 when selected, over a 3px
 *   foreground bar exactly the label's width, on a 36px row, 20px between labels (the pages
 *   revamp's one inner tab row, 2026-10-27). The bar is one element that slides to the selected tab
 *   (transform + width, 480ms ease-out), measured off the tab, not a border per tab.
 * - `boxed` is a LENS on one dataset — Movers' Gainers / Losers / Turnover, Sectors' orderings:
 *   the same rows, re-cut (`4236:104328`). Chip's `boxed` skin, so a boxed tab and a boxed chip
 *   cannot drift apart. Home Movers was boxed here too, on that same reasoning, until the owner
 *   overruled it on 2026-09-29: Home Movers now uses the underline, as its frame always drew,
 *   while Market › Movers keeps boxed — its own frame draws boxed.
 *
 * Its colour is `--tab-indicator`, the foreground in both themes. Each label holds its bold width
 * in reserve (`reserve-bold`), so the 600 selection never widens its tab and the bar never has to
 * chase a moving neighbour.
 *
 * Below `lg` the strip scrolls sideways rather than wrapping: a tab row that wraps to two lines
 * stops reading as one control, and "Turnover" dropping under "Gainers" looks like a second
 * group. The strip's vertical padding, cancelled by an equal negative margin, is room inside the
 * scroller for what is drawn outside a tab — the focus ring 4px out, and a boxed tab's grown hit
 * area (4px at `md`, 8px at `sm`) — so neither is clipped, at no cost to the row's outer metrics.
 * That margin is the strip's, inside an outer box that takes `className`: on one element the
 * strip's `lg:my-0` reset beat a caller's `mb-*` from `lg` up, and the caller's margin vanished.
 * Both kinds share the scroller and the roving-tabindex keyboard; only the tab element differs.
 */
export default function TabBar({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'underline',
  size = 'md',
  idBase,
  className,
  stripClassName,
}: TabBarProps) {
  const { onKeyDown, register } = useRovingTabs(options, value, onChange)
  const stripRef = useRef<HTMLDivElement | null>(null)
  const { containerRef, box } = useSlidingIndicator<HTMLDivElement>(value)
  const activeRef = useRef<HTMLButtonElement | null>(null)
  const boxed = variant !== 'underline'

  // Scrolled into view by hand rather than with scrollIntoView: that API walks
  // every scrollable ancestor, so selecting a tab would also jog the page
  // vertically on a phone where the strip sits mid-screen.
  useEffect(() => {
    const strip = stripRef.current
    const tab = activeRef.current
    if (strip === null || tab === null) return
    if (strip.scrollWidth <= strip.clientWidth) return
    strip.scrollLeft = Math.max(0, tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2)
  }, [value])

  return (
    // A flex column so the strip's negative margins neither collapse into a caller's margin nor
    // leak past this box. `max-lg:min-w-0` lets it shrink in a flex row while the strip scrolls,
    // as the strip alone used to.
    <div className={cn('flex flex-col max-lg:min-w-0', className)}>
      <div
        ref={(element) => {
          stripRef.current = element
          containerRef.current = element
        }}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        className={cn(
          'scrollbar-slim relative flex items-center overflow-x-auto overscroll-x-contain',
          boxed && size === 'sm' ? '-my-2 py-2' : '-my-1 py-1',
          boxed ? 'gap-2' : 'gap-5',
          'lg:my-0 lg:overflow-x-visible lg:py-0',
          stripClassName,
        )}
      >
        {boxed || box === null ? null : (
          // Transform and width only: `left`/`top` are fixed. The bar is 1px wide and scaled, so
          // the slide is a compositor transform; the 3px height never scales.
          <span
            aria-hidden="true"
            className={cn(
              // The 3px bar is a zero-height box's bottom border: a border width is exempt from
              // the 4-grid, where a 3px-tall box is not.
              'pointer-events-none absolute left-0 h-0 w-px origin-left border-b-3 border-tab-indicator',
              box.animate && 'transition-transform duration-slow ease-out',
            )}
            style={{
              top: box.top + box.height - BAR_THICKNESS,
              transform: `translateX(${String(box.left)}px) scaleX(${String(box.width)})`,
            }}
          />
        )}
        {options.map((option) => {
          const selected = option.value === value
          const tabRef = (element: HTMLButtonElement | null) => {
            register(option.value)(element)
            if (selected) activeRef.current = element
          }
          const wiring =
            idBase === undefined
              ? {}
              : { id: tabId(idBase, option.value), 'aria-controls': panelId(idBase) }

          if (boxed) {
            return (
              <Chip
                key={option.value}
                ref={tabRef}
                role="tab"
                aria-selected={selected}
                tabIndex={selected ? 0 : -1}
                {...wiring}
                variant="boxed"
                size={size}
                active={selected}
                onClick={() => {
                  onChange(option.value)
                }}
              >
                {option.label}
              </Chip>
            )
          }

          return (
            <button
              key={option.value}
              ref={tabRef}
              type="button"
              role="tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              {...wiring}
              onClick={() => {
                onChange(option.value)
              }}
              className={cn(
                'relative inline-flex h-11 shrink-0 cursor-pointer items-center whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control lg:h-9',
                // The mock's inner tab: the primary ink dimmed to .55, .8 on hover, .87 selected.
                selected
                  ? 'text-h-m text-tab-ink-active'
                  : 'text-body-l text-tab-ink hover:text-tab-ink-hover focus-visible:text-tab-ink-hover',
              )}
            >
              <span data-text={option.label} className="reserve-bold">
                {option.label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface TabPanelProps {
  /** The same `idBase` the TabBar got. */
  idBase: string
  /** The selected tab's value, so the panel is named by the tab that shows it. */
  value: string
  className?: string
  children: ReactNode
}

// The kit's own fade-in class, put on by hand below: Reveal would play it on mount.
const FADE_IN = revealMotion('fade-in').className ?? ''

/**
 * The panel a `TabBar` with an `idBase` controls. One panel whose content swaps, not one per tab,
 * so it is always labelled by the selected tab. Focusable because a panel whose content opens with
 * text rather than a control would otherwise be skipped by Tab.
 *
 * The panel fades in (220ms, `animate-fade-in`) when `value` changes, and not on its first mount,
 * where the page's own entrance already plays. The class is added by hand after the change rather
 * than set in the markup: a class in the markup would play on mount, and, being an `animate-*`,
 * would also replace the page stagger's fade-up on this very element. It is taken off when it
 * ends so the next change can add it again.
 */
export function TabPanel({ idBase, value, className, children }: TabPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const shown = useRef(value)

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (panel === null || shown.current === value) return
    shown.current = value
    // Taken off and put back across a reflow, so a second change inside the 220ms restarts it.
    panel.classList.remove(FADE_IN)
    panel.getBoundingClientRect()
    panel.classList.add(FADE_IN)
    // `animationend` bubbles, so a child's own animation must not end the panel's fade early.
    const done = (event: AnimationEvent) => {
      if (event.target !== panel) return
      panel.classList.remove(FADE_IN)
      panel.removeEventListener('animationend', done)
    }
    panel.addEventListener('animationend', done)
    return () => {
      panel.removeEventListener('animationend', done)
    }
  }, [value])

  return (
    <div
      ref={panelRef}
      role="tabpanel"
      id={panelId(idBase)}
      aria-labelledby={tabId(idBase, value)}
      tabIndex={0}
      className={cn(
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        className,
      )}
    >
      {children}
    </div>
  )
}
